// ═══════════════════════════════════════════════════════════════
// V·IAnet Governance — Design System (pixel-perfect prototype match)
// ═══════════════════════════════════════════════════════════════
//
// FUENTE DE VERDAD: /DESIGN.md (raíz del repo).
// Este objeto C es el punto de entrada para estilos inline en componentes React.
// Los tokens Tailwind equivalentes viven en packages/frontend/src/index.css (@theme).
// Las CSS variables --via-* en :root cubren componentes que usan var() directo.
//
// Mapa C ↔ DESIGN.md:
//   C.azul1          → azul1          #1F3864 (primary, nav, headers)
//   C.azul2          → azul2          #2F5496 (accent, links, cross-refs)
//   C.celeste        → celeste        #33BCE1 (highlight, focus, badges)
//   C.azulClaro      → azulClaro      #1A94BF (secondary accent, hover)
//   C.pageBg         → fondo          #ECEEF2 (page background)
//   C.surface        → surface-primary #FFFFFF (cards, panels)
//   C.tableAltRow    → table-alt-row  #D6E4F0 (alternating rows)
//   C.success        → success        #10B981 (semantic success)
//   C.warning        → warning        #F59E0B (semantic warning)
//   C.error          → error          #EF4444 (semantic error)
//   C.info           → info           #33BCE1 (alias celeste)
//
// NOTA: C.statusOk/Warn/Error usan tonos MUI legacy (#4CAF50/#FF9800/#F44336)
// y se mantienen por compatibilidad con componentes que heredan paleta MUI.
// Componentes nuevos deben usar C.success/warning/error (valores DESIGN.md).

import type { EstadoDocumento, TipoDocumental, TipologiaProceso } from '../types';
import { DOC_COLORS } from '../config/documentStyles';

// ─── UI COLORS (document-related colors sourced from documentStyles config) ───
export const C = {
  azulMuyOscuro: '#0E243E',
  azulOscuro: '#16365B',
  azulMedio: '#16507D',
  azulClaro: '#1A94BF',
  celeste: '#33BCE1',
  azul1: DOC_COLORS.h1,
  azul2: DOC_COLORS.h2,
  acento: DOC_COLORS.h3,
  headerAccent: DOC_COLORS.pageHeaderAccent,
  bg: '#F8F9FB',
  surface: '#FFFFFF',
  border: '#E2E6ED',
  borderAlt: '#F2F4F6',
  text: '#1A1D23',
  textSec: '#5A6171',
  gray700: '#374151',
  gray500: '#6B7280',
  gray400: '#9CA3AF',
  // ─── Semantic tokens (DESIGN.md) — usar en componentes nuevos ───
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  info: '#33BCE1',
  tableAltRow: '#D6E4F0',
  // ─── Legacy MUI palette — mantener por compat (usar semantic arriba en código nuevo) ───
  statusOk: '#4CAF50', // verde — exito, completo, OK
  statusWarn: '#FF9800', // naranja — advertencia, pendiente
  statusError: '#F44336', // rojo — error, fallo, critico
  statusInfo: '#1E3A5F', // azul oscuro — informacion, neutro
  statusPurple: '#9C27B0', // purpura — destacado, especial
  statusNeutral: '#607D8B', // gris azulado — archivado, inactivo
  textMut: DOC_COLORS.pageHeaderMuted,
  softBlue: DOC_COLORS.h1Border,
  pageBg: '#ECEEF2',
  // Backstage shadows (MUI Paper elevation)
  shadow1:
    '0 2px 1px -1px rgba(0,0,0,.2), 0 1px 1px 0 rgba(0,0,0,.14), 0 1px 3px 0 rgba(0,0,0,.12)',
  shadowHover:
    '0 5px 5px -3px rgba(0,0,0,.2), 0 8px 10px 1px rgba(0,0,0,.14), 0 3px 14px 2px rgba(0,0,0,.12)',
  shadowBar:
    '0 2px 4px -1px rgba(0,0,0,.2), 0 4px 5px 0 rgba(0,0,0,.14), 0 1px 10px 0 rgba(0,0,0,.12)',
  // Backstage radii
  radius: 12,
  radiusSm: 8,
  radiusLg: 16,
} as const;

// ─── STATUS CONFIG for EstadoDocumento ───
export const STATUS_CONFIG: Record<EstadoDocumento, { label: string; color: string; bg: string }> =
  {
    BORRADOR: { label: 'Borrador', color: '#94A3B8', bg: '#F1F5F9' },
    EN_REVISION: { label: 'En Revision', color: '#F59E0B', bg: '#FFFBEB' },
    APROBADO: { label: 'Aprobado', color: '#2563EB', bg: '#EFF6FF' },
    PUBLICADO: { label: 'Vigente', color: '#16A34A', bg: '#F0FDF4' },
    ARCHIVADO: { label: 'Archivado', color: '#9CA3AF', bg: '#F9FAFB' },
  };

// ─── STATUS CONFIG for version states (EstadoDocumento + legacy EstadoVersion) ───
export const VERSION_STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  BORRADOR: { label: 'Borrador', color: '#94A3B8', bg: '#F1F5F9' },
  EN_REVISION: { label: 'En Revision', color: '#F59E0B', bg: '#FFFBEB' },
  ENVIADO: { label: 'Enviado', color: '#F59E0B', bg: '#FFFBEB' }, // legacy alias
  APROBADO: { label: 'Aprobado', color: '#2563EB', bg: '#EFF6FF' },
  PUBLICADO: { label: 'Vigente', color: '#16A34A', bg: '#F0FDF4' },
  ARCHIVADO: { label: 'Archivado', color: '#EF4444', bg: '#FEF2F2' },
  RECHAZADO: { label: 'Rechazado', color: '#EF4444', bg: '#FEF2F2' }, // legacy alias
};

// ─── DOC TYPES ───
export const DOC_TYPES: Record<TipoDocumental, { label: string; level: 'PYRAMID' | 'SUPPORT' }> = {
  PR: { label: 'Procedimiento', level: 'PYRAMID' },
  NC: { label: 'Norma Corporativa', level: 'PYRAMID' },
  IT: { label: 'Instruccion de Trabajo', level: 'PYRAMID' },
  MN: { label: 'Manual', level: 'PYRAMID' },
  PA: { label: 'Plan de Accion', level: 'PYRAMID' },
  RE: { label: 'Reporte', level: 'PYRAMID' },
  FC: { label: 'Ficha de Caracterizacion', level: 'SUPPORT' },
  FL: { label: 'Flujograma', level: 'SUPPORT' },
  HE: { label: 'Herramienta', level: 'SUPPORT' },
  MO: { label: 'Modelo', level: 'SUPPORT' },
  PT: { label: 'Protocolo', level: 'SUPPORT' },
  FR: { label: 'Ficha de Rol Funcional', level: 'SUPPORT' },
  FP: { label: 'Ficha de Puesto', level: 'SUPPORT' },
  FG: { label: 'Ficha de Rol de Gobernanza', level: 'SUPPORT' },
};

// ─── TIPOLOGIA CONFIG ───
export const TIPOLOGIA_CONFIG: Record<TipologiaProceso, { label: string; color: string }> = {
  PE: { label: 'Procesos Estrategicos', color: C.azul1 },
  PO: { label: 'Procesos Operativos', color: C.azul2 },
  PS: { label: 'Procesos de Soporte', color: C.azulClaro },
};

// ─── WORKFLOW STEPS (EstadoDocumento) ───
export const WF_STEPS: { key: EstadoDocumento; label: string; color: string }[] = [
  { key: 'BORRADOR', label: 'Borrador', color: '#94A3B8' },
  { key: 'EN_REVISION', label: 'En Revision', color: '#F59E0B' },
  { key: 'APROBADO', label: 'Aprobado', color: '#2563EB' },
  { key: 'PUBLICADO', label: 'Vigente', color: '#16A34A' },
];

// ─── WORKFLOW TRANSITIONS ───
// Backend state machine: BORRADOR -> EN_REVISION -> APROBADO -> PUBLICADO -> ARCHIVADO
// Actions: ENVIAR, APROBAR, RECHAZAR, PUBLICAR
export const TRANSITIONS: Record<string, { label: string; accion: string; color: string }[]> = {
  BORRADOR: [{ label: 'Enviar a revision', accion: 'ENVIAR', color: '#F59E0B' }],
  EN_REVISION: [
    { label: 'Aprobar', accion: 'APROBAR', color: '#2563EB' },
    { label: 'Rechazar', accion: 'RECHAZAR', color: '#EF4444' },
  ],
  APROBADO: [{ label: 'Publicar', accion: 'PUBLICAR', color: '#16A34A' }],
};

// ─── DIRECTIONS ───
export const DIRECTIONS = ['CEO', 'COO', 'CTO'] as const;

// ─── FORMAT DATE ───
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '\u2014';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' });
}
